import { unsafeCSS, supportsAdoptingStyleSheets, adoptStyles, nothing, html } from "lit";
import { query, state } from "lit/decorators.js";
import { addDays, endOfMonth } from "date-fns";
import VanillaCalendar from "../../../node_modules/@sennder/vanilla-calendar/src/scripts/main.js";
import changeMonth from "../../../node_modules/@sennder/vanilla-calendar/src/scripts/methods/changeMonth.js";
import calendarStyle from "./calendar-base.css.js";
import calendarThemeStyle from "./calendar-theme-light.css.js";
import "../Button/index.js";
import "../IconButton/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { convertToDate, formatStandardDate, rangeHasChanged, parseStandardDate } from "../dates.js";
import { dateListToCalendarDates, getSingleCalendarTemplate, getRangeCalendarTemplate, formatMonthHeader } from "./utils.js";
import { dateRangeAttributeConverter, dateAttributeConverter, dateListAttributeConverter } from "../attribute-converters.js";
import { dateOrStringHasChanged, dateArrayHasChanged } from "../change-detection.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
const calendarStylesheet = unsafeCSS(calendarStyle);
const calendarThemeStylesheet = unsafeCSS(calendarThemeStyle);
let DsfCalendar = class extends BaseComponent {
  constructor() {
    super(...arguments);
    this.value = [];
    this.dsf_mode = "single";
    this.dsf_locale = navigator.language;
    this.dsf_visible_months = 1;
    this.dsf_disabled_dates = [];
    this.dsf_highlight_today = true;
    this.dsf_highlight_weekends = false;
    this.dsf_highlighted_dates = [];
    this.dsf_mobile = false;
    this.dsf_overnight = false;
    this._calendarMonths = [];
    this._canGoPrevMonth = false;
    this._canGoNextMonth = false;
    this._internalUpdate = false;
  }
  get calendar() {
    return this._calendarObj;
  }
  get monthsToRender() {
    return this.dsf_visible_months < 1 ? 1 : this.dsf_visible_months > 12 ? 12 : this.dsf_visible_months;
  }
  get minDate() {
    return convertToDate(this.dsf_min_date);
  }
  get maxDate() {
    return convertToDate(this.dsf_max_date);
  }
  connectedCallback() {
    super.connectedCallback();
    const root = this.shadowRoot;
    if (supportsAdoptingStyleSheets) {
      adoptStyles(root, [...root.adoptedStyleSheets, calendarStylesheet, calendarThemeStylesheet]);
    } else {
      adoptStyles(root, [calendarStylesheet, calendarThemeStylesheet]);
    }
  }
  firstUpdated() {
    this.initCalendar();
  }
  update(changedProps) {
    if (this._calendarObj && changedProps.size > 0) {
      const publicPropsUpdated = [...changedProps.keys()].some((prop2) => !prop2.startsWith("_"));
      const internalValueUpdate = changedProps.size === 1 && changedProps.has("value") && this._internalUpdate;
      if (publicPropsUpdated && !internalValueUpdate) {
        this.updateCalendar();
      }
      if (internalValueUpdate) {
        this.highlightOvernightDay();
      }
      this._internalUpdate = false;
    }
    super.update(changedProps);
  }
  refresh() {
    this.updateCalendar();
  }
  updateCalendar() {
    if (this._calendarObj) {
      this.bindPropsToOptions(this._calendarObj);
      this._calendarObj.reset();
      this.handleCalendarUpdate();
    }
  }
  getBaseCalendarOptions() {
    const options = {
      settings: {
        visibility: {
          theme: "light",
          daysOutside: false,
          disabled: false
        },
        selection: {
          month: true,
          year: true
        },
        range: {},
        selected: {}
      },
      actions: {
        clickDay: (_, dates) => {
          if (!dates || !dates.length) {
            this.setValue([]);
            return;
          }
          if (this.dsf_mode === "single") {
            this.setValue([parseStandardDate(dates[0])]);
          } else {
            if (dates.length > 1) {
              const sorted = (dates == null ? void 0 : dates.sort((a, b) => +parseStandardDate(a) - +parseStandardDate(b))) || [];
              this.setValue([
                parseStandardDate(sorted[0]),
                parseStandardDate(sorted[sorted.length - 1])
              ]);
            }
          }
        }
      },
      DOMTemplates: {
        default: getSingleCalendarTemplate(this),
        multiple: getRangeCalendarTemplate(this)
      }
    };
    return options;
  }
  highlightOvernightDay() {
    if (!this.dsf_overnight) {
      return;
    }
    const selectedEls = [
      ...this.shadowRoot.querySelectorAll(".vanilla-calendar-day_selected")
    ];
    selectedEls.forEach((selectedEl) => {
      if (selectedEl.innerText) {
        selectedEl.classList.add("vanilla-calendar-day_selected-first");
      }
    });
    if (this.value.length > 0) {
      const nextDay = formatStandardDate(addDays(this.value[0], 1));
      const nextDatBtn = this.shadowRoot.querySelector(`[data-calendar-day="${nextDay}"]`);
      const nextDayEl = nextDatBtn == null ? void 0 : nextDatBtn.parentElement;
      if (nextDatBtn && nextDayEl) {
        nextDayEl.classList.add("vanilla-calendar-day_selected");
        nextDayEl.classList.add("vanilla-calendar-day_selected-last");
        nextDatBtn.classList.add("vanilla-calendar-day__btn_selected");
      }
    }
  }
  initCalendar() {
    const options = this.getBaseCalendarOptions();
    this.bindPropsToOptions(options);
    this._calendarObj = new VanillaCalendar(this.calendarElement, options);
    this._calendarObj.init();
    this.handleCalendarUpdate();
  }
  bindPropsToOptions(options) {
    options.type = this.monthsToRender > 1 ? "multiple" : "default";
    options.months = this.monthsToRender;
    options.settings.lang = this.dsf_locale;
    options.settings.selection.day = this.dsf_mode === "range" ? "multiple-ranged" : "single";
    if (this.minDate) {
      options.settings.range.min = formatStandardDate(this.minDate);
    }
    if (this.maxDate) {
      options.settings.range.max = formatStandardDate(this.maxDate);
    }
    const [fromDate, toDate] = this.value;
    if (fromDate) {
      options.settings.selected = {
        dates: [
          toDate ? `${formatStandardDate(fromDate)}:${formatStandardDate(toDate)}` : formatStandardDate(fromDate)
        ],
        month: fromDate.getMonth(),
        year: fromDate.getFullYear()
      };
    } else {
      options.settings.selected = {
        dates: null,
        month: (/* @__PURE__ */ new Date()).getMonth(),
        year: (/* @__PURE__ */ new Date()).getFullYear()
      };
    }
    options.settings.range.disabled = dateListToCalendarDates(this.dsf_disabled_dates);
    options.settings.selected.holidays = dateListToCalendarDates(this.dsf_highlighted_dates);
    options.settings.visibility.today = this.dsf_highlight_today;
    options.settings.visibility.weekend = this.dsf_highlight_weekends;
    options.DOMTemplates.default = getSingleCalendarTemplate(this);
    options.DOMTemplates.multiple = getRangeCalendarTemplate(this);
  }
  handleCalendarUpdate() {
    var _a;
    const selectedMonth = this.calendar.selectedMonth;
    const selectedYear = this.calendar.selectedYear;
    if (selectedMonth === void 0 || selectedYear === void 0) {
      return;
    }
    const headers = [];
    for (let i = 0; i < this.monthsToRender; i++) {
      const nextMonth = selectedMonth + i;
      const normalizedMonth = nextMonth > 11 ? nextMonth - 12 : nextMonth;
      const normalizedYear = nextMonth > 11 ? selectedYear + 1 : selectedYear;
      headers.push({
        header: formatMonthHeader(new Date(normalizedYear, normalizedMonth, 1), this.dsf_locale),
        firstDay: new Date(normalizedYear, normalizedMonth, 1),
        lastDay: endOfMonth(new Date(normalizedYear, normalizedMonth, 1))
      });
    }
    this._calendarMonths = headers;
    const calendarFirstDate = this._calendarMonths[0].firstDay;
    const calendarLastDay = this._calendarMonths[this._calendarMonths.length - 1].lastDay;
    this._canGoPrevMonth = !this.minDate || calendarFirstDate > this.minDate;
    this._canGoNextMonth = !this.maxDate || calendarLastDay < this.maxDate;
    if (this.dsf_mobile) {
      const mobileHeaders = ((_a = this.shadowRoot) == null ? void 0 : _a.querySelectorAll(".calendar-mobile-header")) || [];
      mobileHeaders.forEach((header, index) => {
        var _a2;
        header.textContent = ((_a2 = this._calendarMonths[index]) == null ? void 0 : _a2.header) || "";
      });
    }
    this.highlightOvernightDay();
  }
  setValue(value) {
    this._internalUpdate = true;
    this.value = value;
    this.emit({ name: "input", payload: this.value });
  }
  handlePrevMonthClick() {
    changeMonth(this.calendar, "prev");
    this.handleCalendarUpdate();
  }
  handleNextMonthClick() {
    changeMonth(this.calendar, "next");
    this.handleCalendarUpdate();
  }
  renderDesktopNavPanel() {
    if (this.dsf_mobile) {
      return nothing;
    }
    const btnPrev = html`
      <dsf-icon-button
        @click=${this.handlePrevMonthClick}
        dsf_icon="ChevronLeft"
        ?disabled=${!this._canGoPrevMonth}
        .dsf_title=${this.dsf_prev_button_title}
        data-prev-button
      ></dsf-icon-button>
    `;
    const btnNext = html`
      <dsf-icon-button
        @click=${this.handleNextMonthClick}
        dsf_icon="ChevronRight"
        ?disabled=${!this._canGoNextMonth}
        .dsf_title=${this.dsf_next_button_title}
        data-next-button
      ></dsf-icon-button>
    `;
    let navPanel = this._calendarMonths.length ? html`<div class="mb-6 flex w-full items-center justify-between">
          ${btnPrev}
          <span data-month-label
            >${this._calendarMonths[0] ? this._calendarMonths[0].header : ""}</span
          >
          ${btnNext}
        </div>` : nothing;
    if (this.monthsToRender > 1) {
      const btnGroups = [];
      for (let i = 0; i < this._calendarMonths.length; i++) {
        btnGroups.push(
          html`<div class="flex flex-[1_1_auto] items-center justify-between">
            ${btnPrev}
            <span>${this._calendarMonths[i].header}</span>
            ${btnNext}
          </div>`
        );
      }
      navPanel = html`<div class="mb-6 flex w-full items-center gap-24">${btnGroups}</div> `;
    }
    return navPanel;
  }
  renderMobileNavButtons() {
    const btnPrev = this.dsf_mobile ? html`
          <dsf-button
            class="mb-16"
            dsf_level="secondary"
            @click=${this.handlePrevMonthClick}
            ?disabled=${!this._canGoPrevMonth}
            data-prev-button
          >
            ${this.dsf_prev_button_title}
          </dsf-button>
        ` : nothing;
    const btnNext = this.dsf_mobile ? html`
          <dsf-button
            class="mt-16"
            dsf_level="secondary"
            @click=${this.handleNextMonthClick}
            ?disabled=${!this._canGoNextMonth}
            data-next-button
          >
            ${this.dsf_next_button_title}
          </dsf-button>
        ` : nothing;
    return [btnPrev, btnNext];
  }
  render() {
    const desktopNavPanel = this.renderDesktopNavPanel();
    const [mobileBtnPrev, mobileBtnNext] = this.renderMobileNavButtons();
    return html`
      <div
        class=${`dsf-text-s flex flex-col font-sans text-neutral-700 ${this.dsf_mobile && "flex-auto"}`}
      >
        ${desktopNavPanel} ${mobileBtnPrev}
        <div class=${this.dsf_mobile ? "calendar-mobile" : ""}>
          <div id="calendar"></div>
        </div>
        ${mobileBtnNext}
      </div>
    `;
  }
};
__decorateClass([
  prop({
    type: String,
    reflect: true,
    converter: dateRangeAttributeConverter,
    hasChanged: rangeHasChanged
  })
], DsfCalendar.prototype, "value", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfCalendar.prototype, "dsf_mode", 2);
__decorateClass([
  prop({ type: String })
], DsfCalendar.prototype, "dsf_locale", 2);
__decorateClass([
  prop({ type: Number, validator: (val) => val >= 1 && val <= 12 })
], DsfCalendar.prototype, "dsf_visible_months", 2);
__decorateClass([
  prop({
    type: String,
    converter: dateAttributeConverter,
    hasChanged: dateOrStringHasChanged
  })
], DsfCalendar.prototype, "dsf_min_date", 2);
__decorateClass([
  prop({
    type: String,
    converter: dateAttributeConverter,
    hasChanged: dateOrStringHasChanged
  })
], DsfCalendar.prototype, "dsf_max_date", 2);
__decorateClass([
  prop({
    type: Array,
    converter: dateListAttributeConverter,
    hasChanged: dateArrayHasChanged
  })
], DsfCalendar.prototype, "dsf_disabled_dates", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCalendar.prototype, "dsf_highlight_today", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCalendar.prototype, "dsf_highlight_weekends", 2);
__decorateClass([
  prop({ type: String })
], DsfCalendar.prototype, "dsf_prev_button_title", 2);
__decorateClass([
  prop({ type: String })
], DsfCalendar.prototype, "dsf_next_button_title", 2);
__decorateClass([
  prop({
    type: Array,
    converter: dateListAttributeConverter,
    hasChanged: dateArrayHasChanged
  })
], DsfCalendar.prototype, "dsf_highlighted_dates", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCalendar.prototype, "dsf_mobile", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCalendar.prototype, "dsf_overnight", 2);
__decorateClass([
  query("#calendar")
], DsfCalendar.prototype, "calendarElement", 2);
__decorateClass([
  state()
], DsfCalendar.prototype, "_calendarMonths", 2);
DsfCalendar = __decorateClass([
  defineElement("dsf-calendar")
], DsfCalendar);
export {
  DsfCalendar
};
